import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useToasts } from 'react-toast-notifications';
import Card, { CardDescription, CardFooter, CardFooterDate, CardHeader, CardMenu } from '../../components/Card';
import EmptyState from '../../components/EmptyState';
import Loader from '../../components/Loader';
import IconBrowser from '../../components/icons/IconBrowser';
import IconBrowserGoogle from '../../components/icons/IconBrowserGoogle';
import { ROUTES } from '../../routes/routes';
import { GatedContentConfiguration, GatedSheetsConfiguration } from '../../types/gatedContent';
import { useAxios } from '../../utils/hooks';
import { GatedContentHeader } from './GatedContentHeader';

import { Menu } from '@mantine/core';
import { isGSheet } from '../../common/gatedContentUtils';

export default function GatedContent() {
    const { addToast } = useToasts();
    const axios = useAxios();
    const [gatedContent, setGatedContent] = useState<(GatedContentConfiguration | GatedSheetsConfiguration)[]>();
    useEffect(() => {
        axios.current
            ?.get<GatedContentConfiguration[]>('/gated-content')
            .then((res) => {
                setGatedContent(res.data);
            })
            .catch((err) =>
                addToast('Something went wrong. Please reload the page and try again', { appearance: 'error' })
            );
    }, [axios, addToast]);

    const handleDeleted = (gatedContent: GatedContentConfiguration) => {
        setGatedContent((prev) => prev?.filter((item) => item.id !== gatedContent.id));
    };

    return (
        <div className="p-4 w-full text-center">
            <GatedContentHeader />
            <div className="p-4">
                {!gatedContent && <Loader full />}
                {gatedContent?.length === 0 && (
                    <EmptyState
                        body={
                            <div className="md:w-1/2 m-auto py-4">
                                <h4 className="text-xl">No Gated Content</h4>
                                <span>
                                    You can restrict access to specific urls to paying users by creating Gated Content
                                </span>
                                <div className="md:w-48 m-auto mt-6">
                                    <CreateButton />
                                </div>
                            </div>
                        }
                    />
                )}
                {gatedContent && gatedContent.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {gatedContent.map((gatedContent) => (
                            <GatedContentCard
                                key={gatedContent.id}
                                gatedContent={gatedContent}
                                onDeleted={() => handleDeleted(gatedContent)}
                            />
                        ))}
                        <div className="md:w-48 m-auto mt-6">
                            <CreateButton />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function GatedContentCard({
    gatedContent,
    onDeleted,
}: {
    gatedContent: GatedContentConfiguration;
    onDeleted: () => void;
}) {
    const axios = useAxios();
    const history = useHistory();
    const handleClickChoose = () => {
        isGSheet(gatedContent)
            ? history.push(ROUTES.GATED_CONTENT_SHEETS_EDIT.replace(':id', gatedContent.id))
            : history.push(ROUTES.GATED_CONTENT_EDIT.replace(':id', gatedContent.id));
    };
    const handleDeleteClick = () => {
        window.confirm('Are you sure you want to delete this Gated Content?') &&
            axios.current?.delete(`/gated-content/${gatedContent.id}`).then(onDeleted);
    };

    const handleClickCopySnippet = () => history.push(ROUTES.GATED_CONTENT_SNIPPET.replace(':id', gatedContent.id));

    return (
        <Card>
            <CardHeader onClick={handleClickChoose}>
                {isGSheet(gatedContent) ? <IconBrowserGoogle className="p-8" /> : <IconBrowser className="p-8" />}
            </CardHeader>
            <CardDescription
                title={
                    <button
                        onClick={handleClickChoose}
                        className="font-medium text-lg inline-block mb-2 hover:text-blue-400"
                    >
                        {gatedContent.name}
                    </button>
                }
            />
            <CardFooter>
                <CardFooterDate date={gatedContent.createdAt} />
                <span className="py-1 text-xs font-regular text-gray-900 mr-1 flex flex-row items-center pl-4 hover:text-gray-600">
                    <svg className="h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                        ></path>
                    </svg>

                    <button onClick={handleClickCopySnippet} className="ml-1 relative">
                        Copy Snippet
                    </button>
                </span>

                <button
                    onClick={handleClickChoose}
                    className="ml-4 relative text-xs font-regular text-gray-900 hover:text-gray-600"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 1792 1792"
                        className="inline h-5 fill-current mr-1"
                        stroke="currentColor"
                    >
                        <path d="m888 1184 116-116-152-152-116 116v56h96v96h56zm440-720q-16-16-33 1L945 815q-17 17-1 33t33-1l350-350q17-17 1-33zm80 594v190q0 119-84.5 203.5T1120 1536H288q-119 0-203.5-84.5T0 1248V416q0-119 84.5-203.5T288 128h832q63 0 117 25 15 7 18 23 3 17-9 29l-49 49q-14 14-32 8-23-6-45-6H288q-66 0-113 47t-47 113v832q0 66 47 113t113 47h832q66 0 113-47t47-113v-126q0-13 9-22l64-64q15-15 35-7t20 29zm-96-738 288 288-672 672H640V992zm444 132-92 92-288-288 92-92q28-28 68-28t68 28l152 152q28 28 28 68t-28 68z" />
                    </svg>
                    Edit
                </button>
                <CardMenu
                    items={[
                        { title: 'Edit', onClick: handleClickChoose, color: 'GREY' },
                        { title: 'Delete', onClick: handleDeleteClick },
                    ]}
                />
            </CardFooter>
        </Card>
    );
}

function CreateButton() {
    const history = useHistory();
    const navigateTo = (route: keyof typeof ROUTES) => () => history.push(ROUTES[route]);
    return (
        <Menu withArrow>
            <Menu.Target>
                <button className="text-white text-sm font-medium leading-5 rounded shadow-lg bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 py-2 px-4">
                    Create Gated Content
                </button>
            </Menu.Target>
            <Menu.Dropdown>
                <Menu.Label>Choose one</Menu.Label>
                <Menu.Item onClick={navigateTo('GATED_CONTENT_NEW')}>Webpage/Website</Menu.Item>
                <Menu.Item onClick={navigateTo('GATED_CONTENT_SHEETS')}>Google Sheet</Menu.Item>
            </Menu.Dropdown>
        </Menu>
    );
}
